import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useState } from 'react';

export type PlacementRenderProps = {
	onDismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Type constraint issue with CustomRenderer - PlacementRenderProps is more specific than AnyProps
const placementConfig: PlacementBrowserConfig = {
	placementId: 'jira-issue-view-twc-spotlight-tour',
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>((Messages) => ({ preloadedInitialData }) => {
	const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage>;
	}>('/', {
		autoRefetch: {
			isEnabled: !preloadedInitialData,
			updateKey: 'jira-issue-view-twc-spotlight-tour',
		},
	});

	const messages = preloadedInitialData?.messages || data?.messages;

	const visibleMessages = messages
		?.filter((message) => !dismissedIds.has(message.messageInstanceId))
		?.slice?.(0, 1); // Only show one message at most

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedIds((prev) => new Set([...Array.from(prev), messageInstanceId]));
	};

	if (error) {
		throw error;
	}

	if (!preloadedInitialData && isLoading) {
		return null;
	}

	if (!visibleMessages || visibleMessages.length === 0) {
		return null;
	}

	const renderedMessages = visibleMessages.map((message) => (
		<Messages.render
			key={message.messageInstanceId}
			onDismiss={() => handleDismiss(message.messageInstanceId)}
			{...message}
		/>
	));

	return <>{renderedMessages}</>;
});
